import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Rocket, ArrowRight } from "lucide-react";

export default function ApproveUserPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, already_approved, error
  const [message, setMessage] = useState("");
  const token = searchParams.get("token");

  useEffect(() => {
    const approveUser = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Token de aprobación no proporcionado");
        return;
      }

      try {
        const result = await api.approveUserByToken(token);
        if (result.already_approved) {
          setStatus("already_approved");
          setMessage(result.message);
        } else {
          setStatus("success");
          setMessage(result.message);
        }
      } catch (error) {
        setStatus("error");
        const errorMsg = error.response?.data?.detail || "Error al aprobar el usuario";
        setMessage(errorMsg);
      }
    };

    approveUser();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="approve-user-page">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center mb-4">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Innovation OS</CardTitle>
          <CardDescription>Aprobación de Usuario</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === "loading" && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Procesando aprobación...</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">¡Usuario Aprobado!</h3>
              <p className="text-muted-foreground mb-6">{message}</p>
              <p className="text-sm text-muted-foreground">
                El usuario recibirá un email de confirmación y podrá acceder a la plataforma.
              </p>
            </div>
          )}

          {status === "already_approved" && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-blue-600 mb-2">Usuario Ya Aprobado</h3>
              <p className="text-muted-foreground">{message}</p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-red-600 mb-2">Error</h3>
              <p className="text-muted-foreground">{message}</p>
            </div>
          )}

          <Button 
            className="w-full" 
            onClick={() => navigate("/login")}
            data-testid="go-to-login-btn"
          >
            Ir a Iniciar Sesión
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
